import { lazy, type ComponentType } from 'react';

// ─────────────────────────────────────────────────────────────────────
// A LAZY CHUNK THAT IS GONE AFTER A DEPLOY RELOADS THE PAGE ONCE (#1145).
//
// The bundle is split (AUD-035), so a tab loaded before a deploy can ask the
// new image for a chunk hash only the OLD image had — the same two-deploys race
// the boot watchdog handles for the main module (#579, index.html). That
// listener cannot see this case: a failed `import()` rejects a promise, it
// fires no element `error` event. So the loader does it, with the SAME rule
// and the SAME memory, so the two can never reload one page twice:
//
//   - one reload per 30 seconds, remembered in `sessionStorage` under the key
//     the watchdog uses; `index.html` is served `no-store`, so the reload is
//     guaranteed current HTML and therefore chunks that exist;
//   - no memory, no reload — a loop guard that cannot remember is not a guard;
//   - otherwise the error is rethrown, and the screen shows it.
// ─────────────────────────────────────────────────────────────────────

/** The watchdog's own key and window (index.html). */
export const ASSET_RELOAD_KEY = 'starpoint_asset_reload_v1';
export const ASSET_RELOAD_WINDOW_MS = 30_000;

/**
 * Whether a failed load may reload the page — and, if so, records that it has.
 * Exported for the test; `reload` is injected for the same reason.
 */
export function reloadOnceForMissingChunk(
  reload: () => void = () => window.location.reload(),
  now: number = Date.now(),
): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(ASSET_RELOAD_KEY) || 0);
    if (now - last < ASSET_RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(ASSET_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** `React.lazy`, with the reload-once rule above for a chunk that did not arrive. */
export function lazyWithReload<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(() =>
    load().catch((error: unknown) => {
      // Reloading: never settle, so nothing renders an error in the moment
      // before the page goes.
      if (reloadOnceForMissingChunk()) return new Promise<never>(() => {});
      throw error;
    }),
  );
}
